/**
 * priceHistoryChart — the geometry behind the card price graph.
 *
 * Kept pure (no React, no SVG) so the one rule that matters is unit-testable:
 * days we never sampled must render as GAPS. The provider is polled daily-ish
 * and often less for mid-value cards, so a single continuous line through the
 * whole window would draw prices that were never quoted.
 *
 * A charting library would happily interpolate straight through those holes,
 * which is why this is hand-rolled.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Consecutive observations further apart than this start a NEW line segment
 * instead of continuing the old one. Three days tolerates a weekend of missed
 * polls without pretending a three-week hole was a smooth trend.
 */
export const GAP_DAYS = 3;

const toTime = (value) => new Date(value).getTime();

/**
 * Split observations into continuous runs, breaking wherever the sampling gap
 * exceeds `gapDays`.
 */
export function splitIntoSegments(points = [], gapDays = GAP_DAYS) {
  const valid = (Array.isArray(points) ? points : []).filter(
    (p) => p && Number.isFinite(Number(p.price)) && Number.isFinite(toTime(p.observedAt))
  );
  if (valid.length === 0) return [];

  const segments = [[valid[0]]];
  for (let i = 1; i < valid.length; i += 1) {
    const gap = toTime(valid[i].observedAt) - toTime(valid[i - 1].observedAt);
    if (gap > gapDays * DAY_MS) segments.push([valid[i]]);
    else segments[segments.length - 1].push(valid[i]);
  }
  return segments;
}

/**
 * Project observations into SVG coordinates.
 *
 * Returns segments of {x, y, point} plus the domain, so the caller can label the
 * axes with real numbers rather than guessing at the scale.
 */
export function buildChartSeries(points = [], options = {}) {
  const {
    width = 320,
    height = 140,
    padding = 8,
    gapDays = GAP_DAYS,
  } = options;

  const segments = splitIntoSegments(points, gapDays);
  const flat = segments.flat();

  if (flat.length === 0) {
    return { segments: [], min: null, max: null, first: null, last: null, isFlat: false };
  }

  const prices = flat.map((p) => Number(p.price));
  const times = flat.map((p) => toTime(p.observedAt));
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);

  const innerW = Math.max(1, width - padding * 2);
  const innerH = Math.max(1, height - padding * 2);
  const timeSpan = maxTime - minTime;
  const priceSpan = maxPrice - minPrice;
  // A single observation, or a price that never moved, sits on the mid-line
  // rather than dividing by zero or hugging an edge.
  const isFlat = priceSpan === 0;

  const project = (p) => {
    const t = toTime(p.observedAt);
    const x = timeSpan === 0 ? padding + innerW / 2 : padding + ((t - minTime) / timeSpan) * innerW;
    const y = isFlat
      ? padding + innerH / 2
      : padding + innerH - ((Number(p.price) - minPrice) / priceSpan) * innerH;
    return { x, y, point: p };
  };

  return {
    segments: segments.map((seg) => seg.map(project)),
    min: minPrice,
    max: maxPrice,
    first: flat[0],
    last: flat[flat.length - 1],
    isFlat,
  };
}

/** `M x y L x y …` for one segment. A one-point segment gets no path (draw a dot). */
export function segmentPath(segment = []) {
  if (!Array.isArray(segment) || segment.length < 2) return "";
  return segment
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(" ");
}

/**
 * Header copy for the graph.
 *
 * Reports the OBSERVED window ("4 observations over 90 days") rather than
 * implying continuous coverage, and refuses to state a trend from a single
 * point — one observation is not a direction.
 */
export function historyHeadline({ points = [], days = 90 } = {}) {
  const count = Array.isArray(points) ? points.length : 0;
  if (count === 0) return "No price observations yet";
  const label = `${count} observation${count === 1 ? "" : "s"} over ${days} days`;
  if (count === 1) return `${label} · not enough history to show a trend`;
  return label;
}

/** Direction + magnitude between the first and last observation. */
export function priceChange(points = []) {
  const valid = (Array.isArray(points) ? points : []).filter((p) =>
    Number.isFinite(Number(p?.price))
  );
  if (valid.length < 2) return { direction: "flat", abs: 0, pct: null };
  const first = Number(valid[0].price);
  const last = Number(valid[valid.length - 1].price);
  const abs = last - first;
  const pct = first > 0 ? (abs / first) * 100 : null;
  const direction = abs > 0 ? "up" : abs < 0 ? "down" : "flat";
  return { direction, abs, pct };
}
